import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import backend from '../lib/backend';
import personalInformationStepForm from '../state/forms/eslForms/personalInformationStepForm';
import { CommonApplicationProfile } from '../types/commonApplicationProfile';
import { ValidationError } from '../types/errors/validation';

const usePersonalInformationStepForm = () => {
  const fields = personalInformationStepForm.fields;

  const values = {
    applicant_first_name: useRecoilValue(fields.applicant_first_name.valueState),
    applicant_last_name: useRecoilValue(fields.applicant_last_name.valueState),
    applicant_phone_number: useRecoilValue(fields.applicant_phone_number.valueState),
    applicant_email: useRecoilValue(fields.applicant_email.valueState),
    citizenship_status: useRecoilValue(fields.citizenship_status.valueState),
    address_line: useRecoilValue(fields.address_line.valueState),
    address_detail: useRecoilValue(fields.address_detail.valueState),
    city: useRecoilValue(fields.city.valueState),
    state: useRecoilValue(fields.state.valueState),
    zip: useRecoilValue(fields.zip.valueState),
  };

  const validStates = {
    applicant_first_name: useRecoilValue(fields.applicant_first_name.validState),
    applicant_last_name: useRecoilValue(fields.applicant_last_name.validState),
    applicant_phone_number: useRecoilValue(fields.applicant_phone_number.validState),
    applicant_email: useRecoilValue(fields.applicant_email.validState),
    citizenship_status: useRecoilValue(fields.citizenship_status.validState),
    address_line: useRecoilValue(fields.address_line.validState),
    address_detail: useRecoilValue(fields.address_detail.validState),
    city: useRecoilValue(fields.city.validState),
    state: useRecoilValue(fields.state.validState),
    zip: useRecoilValue(fields.zip.validState),
  };

  const setFirstName = useSetRecoilState(fields.applicant_first_name.valueState);
  const setLastName = useSetRecoilState(fields.applicant_last_name.valueState);
  const setPhone = useSetRecoilState(fields.applicant_phone_number.valueState);
  const setEmail = useSetRecoilState(fields.applicant_email.valueState);
  const setCitizenship = useSetRecoilState(fields.citizenship_status.valueState);
  const setAddress = useSetRecoilState(fields.address_line.valueState);
  const setApt = useSetRecoilState(fields.address_detail.valueState);
  const setCity = useSetRecoilState(fields.city.valueState);
  const setState = useSetRecoilState(fields.state.valueState);
  const setZip = useSetRecoilState(fields.zip.valueState);

  const setFieldsFromProfile = useCallback((profile: CommonApplicationProfile) => {
    setFirstName(profile.student_first_name ?? '');
    setLastName(profile.student_last_name ?? '');
    setPhone(profile.student_phone_number ?? '');
    setEmail(profile.student_email ?? '');
    setCitizenship(profile.citizenship_status ?? null);
    setAddress(profile.address_line ?? '');
    setApt(profile.address_detail ?? '');
    setCity(profile.city ?? '');
    setState(profile.state ?? null);
    setZip(profile.zip ?? '');
  }, [
    setFirstName,
    setLastName,
    setPhone,
    setEmail,
    setCitizenship,
    setAddress,
    setApt,
    setCity,
    setState,
    setZip,
  ]);

  const updateCommonProfile = async () => {
    const payload = {
      us_citizenship_status: values.citizenship_status?.code,
      address_line: values.address_line,
      address_detail: values.address_detail,
      city: values.city,
      state: values.state?.abbreviation,
      zip: values.zip,
    };

    const { data } = await backend.updateCommonApplicationProfile(payload);

    return data;
  };

  const updateUserProfile = async () => {
    const requestData = {
      first_name: values.applicant_first_name,
      last_name: values.applicant_last_name,
      mobile_number: values.applicant_phone_number,
      name: `${values.applicant_first_name} ${values.applicant_last_name}`,
    };

    await backend.updateUser(requestData);
  };

  const submitForm = async () => {
    const isValid = Object.values(validStates).every(Boolean);

    if (!isValid) throw new ValidationError();

    await updateUserProfile();

    return await updateCommonProfile();
  };

  return {
    submitForm,
    setFieldsFromProfile,
    fields,
    validStates,
  };
};

export default usePersonalInformationStepForm;

